import { useCallback, useState } from "react";
import { styled } from "@mui/material/styles";
import { Checkbox } from "@mui/material";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";

import { Priority as OptionType } from "interfaces/priority";
import { Autocomplete } from "components/Autocomplete";
import { useLazyGetPriority } from "hooks/case-priority-types/useLazyGetPriority";
import { filterOptions } from "components/CasesTable/Filters/utils/filterOptions";

interface PriorityFilterProps {
  selectedPriorities: OptionType[];
  onChangeSelectedPriorities: (values: OptionType[]) => void;
  showChips?: boolean;
}

const selectAllOption: OptionType = {
  id: -1,
  name: "Select All",
  order: 0
};

export const PriorityFilter = ({
  selectedPriorities,
  onChangeSelectedPriorities,
  showChips = true
}: PriorityFilterProps): JSX.Element => {
  const {
    loading: priorityLoading,
    error: priorityError,
    fetchPriority: getPriorities
  } = useLazyGetPriority();
  const [options, setOptions] = useState<OptionType[] | undefined>(undefined);
  const [selectedState, setSelectedState] = useState<OptionType[]>([]);
  const [inputValue, setInputValue] = useState("");

  const onFocus = useCallback(() => {
    /** If it is the first focus event, get options. */
    if (options === undefined) {
      getPriorities().then(response => {
        if (response.results) {
          setOptions([selectAllOption, ...response.results]);
        }
      });
    }
    setSelectedState(selectedPriorities);
  }, [options, getPriorities, selectedPriorities]);

  return (
    <StyledAutocomplete
      label="Priority"
      placeholder={showChips ? "Priority" : ""}
      loading={priorityLoading}
      error={priorityError}
      showChips={showChips}
      size="small"
      multiple
      autoHighlight
      disableCloseOnSelect
      disableClearable
      autoComplete
      onFocus={onFocus}
      options={options ?? []}
      noOptionsText="No results found"
      getOptionLabel={(option: OptionType) => option.name}
      isOptionEqualToValue={(option: OptionType, value: OptionType) =>
        option.name === value.name
      }
      value={selectedPriorities}
      onInputChange={(
        _event: React.ChangeEvent,
        newInputValue: string
      ): void => {
        if (inputValue !== newInputValue) {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={filterOptions}
      renderOption={(
        props: any,
        option: OptionType,
        { selected }: { selected: any }
      ) => {
        let { key, ...optionProps } = props;

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );

        const isSelected =
          option.name.toLowerCase() === "select all"
            ? isEqual(filteredSelectedState, filteredNonSelectAllOptions)
            : selected;

        optionProps = {
          ...optionProps,
          "aria-selected": isSelected
        };

        return (
          <li key={key} {...optionProps}>
            <Checkbox checked={isSelected} />
            {option.name}
          </li>
        );
      }}
      onChange={(_event: React.ChangeEvent, values: OptionType[]) => {
        if (!options?.length) {
          return;
        }

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );

        if (values.some(value => value.name.toLowerCase() === "select all")) {
          // If current selection includes "Select All"
          if (isEqual(filteredSelectedState, filteredNonSelectAllOptions)) {
            // If everything was selected, deselect all
            const selected = selectedState.filter(
              // option => !filteredSelectedState.includes(option)
              option =>
                !filteredSelectedState.some(
                  selected => selected.id === option.id
                )
            );

            onChangeSelectedPriorities(selected);
            setSelectedState(selected);
          } else {
            const selected = uniqBy(
              [...selectedPriorities, ...filteredNonSelectAllOptions],
              "id"
            );

            // If not everything was selected, select all
            onChangeSelectedPriorities(selected);
            setSelectedState(selected);
          }
        } else {
          // Normal selection of individual items
          onChangeSelectedPriorities(values);
          setSelectedState(values);
        }
      }}
    />
  );
};

const StyledAutocomplete = styled(Autocomplete)`
  height: 100%;
`;
